import type { MarketVariants } from '@/lib/i18n'

export type Slide3GoogleYouCopy = {
  /** Profile-card mockup. The name falls back to this when no first name is stored. */
  profileFallbackName: string
  profileStatus: string
  privateBadge: string

  title: string
  body: string
  ruleOfThumb: string
  guideButton: string

  /** Privacy guide slideover. */
  guideEyebrow: string
  closeAria: string
  guideTitle: string
  setPrivateHeading: string
  facebookName: string
  facebookDesc: string
  instagramName: string
  instagramDesc: string

  /** Instagram step 1, split around the two inline icons and emphasis spans:
   *  "{lead}[icon]{more}{mid}[icon]{settings}{tail}". */
  igStep1Lead: string
  igStep1More: string
  igStep1Mid: string
  igStep1Settings: string
  igStep1Tail: string
  /** Step 2: "{lead}{emph}{tail}". */
  igStep2Lead: string
  igStep2Emph: string
  igStep2Tail: string
  /** Step 3: "{lead}[toggle icon]{toggle}{mid}{emph}{tail}". */
  igStep3Lead: string
  igStep3Toggle: string
  igStep3Mid: string
  igStep3Emph: string
  igStep3Tail: string
  /** Step 4: "{lead}{emph}{tail}". */
  igStep4Lead: string
  igStep4Emph: string
  igStep4Tail: string
  igHelpLink: string

  reviewHeading: string
  /** Body split around the "View As" link: "{before}{link}{after}". */
  reviewBefore: string
  reviewLink: string
  reviewAfter: string
  quizzesHeading: string
  quizzesBody: string
  shareHeading: string
  shareBody: string
}

export const copy: MarketVariants<Slide3GoogleYouCopy> = {
  US: {
    profileFallbackName: 'Facebook',
    profileStatus: 'Public profile',
    privateBadge: 'Private',

    title: 'Keep your Facebook and Instagram accounts private.',
    body:
      'Scammers use public posts to learn your routines, family names, and even your travel plans to make their scams more believable.',
    ruleOfThumb:
      'Rule of thumb: if you wouldn’t put it on a postcard, don’t post it online.',
    guideButton: 'How to lock down your social profiles',

    guideEyebrow: 'Privacy guide',
    closeAria: 'Close',
    guideTitle: 'Lock down your social media',
    setPrivateHeading: 'Set your profiles to private',
    facebookName: 'Facebook',
    facebookDesc: 'Run the Privacy Checkup walkthrough.',
    instagramName: 'Instagram',
    instagramDesc: 'Four taps in the app to go private.',

    igStep1Lead: 'Tap',
    igStep1More: 'More',
    igStep1Mid: ' in the bottom left, then tap',
    igStep1Settings: 'Settings',
    igStep1Tail: '.',
    igStep2Lead: 'Tap ',
    igStep2Emph: 'Account Privacy',
    igStep2Tail: ' under “Who can see your content.”',
    igStep3Lead: 'Tap the',
    igStep3Toggle: 'toggle',
    igStep3Mid: ' next to ',
    igStep3Emph: 'Private account',
    igStep3Tail: '.',
    igStep4Lead: 'Tap ',
    igStep4Emph: 'Switch to private',
    igStep4Tail: ' to confirm.',
    igHelpLink: 'Open Instagram’s help article',

    reviewHeading: 'Review what’s already public',
    reviewBefore:
      'Check your profile as a stranger would see it. On Facebook, use the ',
    reviewLink: '“View As” feature',
    reviewAfter:
      '. Remove your phone number, email, and birthday from public view. Those are exactly the details scammers use to impersonate you.',
    quizzesHeading: 'Skip the quizzes and games',
    quizzesBody:
      '“What’s your rockstar name?” style posts are designed to harvest answers to your security questions. Your first pet, your mother’s maiden name, the street you grew up on. Keep these types of personal details private.',
    shareHeading: 'Think before you share',
    shareBody:
      'Holiday photos tell thieves you’re away from home. Check-ins broadcast your location. Posts about grandchildren give scammers names to use in impersonation calls. You can still share, just be thoughtful about the timing and the audience.',
  },
  ar: {
    profileFallbackName: 'فيسبوك',
    profileStatus: 'ملف شخصي عام',
    privateBadge: 'خاص',

    title: 'اجعل حساباتك على فيسبوك وإنستغرام خاصة.',
    body:
      'يستخدم المحتالون المنشورات العامة لمعرفة روتينك اليومي وأسماء أفراد عائلتك، وحتى خطط سفرك، ليجعلوا احتيالهم أكثر إقناعاً.',
    ruleOfThumb:
      'قاعدة بسيطة: ما لا تكتبه على بطاقة بريدية مكشوفة، لا تنشره على الإنترنت.',
    guideButton: 'كيف تحمي حساباتك على وسائل التواصل',

    guideEyebrow: 'دليل الخصوصية',
    closeAria: 'إغلاق',
    guideTitle: 'أحكم حماية حساباتك على وسائل التواصل',
    setPrivateHeading: 'اجعل ملفاتك الشخصية خاصة',
    facebookName: 'فيسبوك',
    facebookDesc: 'شغّل أداة "فحص الخصوصية" واتبع الخطوات.',
    instagramName: 'إنستغرام',
    instagramDesc: 'أربع نقرات في التطبيق ليصبح حسابك خاصاً.',

    igStep1Lead: 'اضغط على',
    igStep1More: 'المزيد',
    igStep1Mid: ' في أسفل القائمة الجانبية، ثم اضغط على',
    igStep1Settings: 'الإعدادات',
    igStep1Tail: '.',
    igStep2Lead: 'اضغط على ',
    igStep2Emph: 'خصوصية الحساب',
    igStep2Tail: ' ضمن "من يمكنه رؤية المحتوى الخاص بك".',
    igStep3Lead: 'فعّل',
    igStep3Toggle: 'مفتاح التبديل',
    igStep3Mid: ' بجانب ',
    igStep3Emph: 'حساب خاص',
    igStep3Tail: '.',
    igStep4Lead: 'اضغط على ',
    igStep4Emph: 'التحويل إلى حساب خاص',
    igStep4Tail: ' للتأكيد.',
    igHelpLink: 'افتح مقالة المساعدة في إنستغرام',

    reviewHeading: 'راجع ما هو معروض للعامة الآن',
    reviewBefore:
      'تصفّح ملفك الشخصي كما يراه شخص غريب. على فيسبوك، استخدم ميزة ',
    reviewLink: '"العرض كـ" (View As)',
    reviewAfter:
      '. أزل رقم هاتفك وبريدك الإلكتروني وتاريخ ميلادك من العرض العام. فهذه بالضبط التفاصيل التي يستخدمها المحتالون لانتحال شخصيتك.',
    quizzesHeading: 'تجاهل الاختبارات والألعاب',
    quizzesBody:
      'المنشورات من نوع "ما اسمك لو كنت نجماً؟" مصمّمة لجمع إجابات أسئلة الأمان الخاصة بك: اسم أول حيوان أليف لديك، واسم عائلة والدتك، والشارع الذي نشأت فيه. احتفظ بهذه التفاصيل الشخصية لنفسك.',
    shareHeading: 'فكّر قبل أن تشارك',
    shareBody:
      'صور العطلة تخبر اللصوص بأنك بعيد عن المنزل. وتسجيل الوصول يعلن موقعك. والمنشورات عن الأحفاد تمنح المحتالين أسماء يستخدمونها في مكالمات انتحال الشخصية. يمكنك أن تشارك ما تريد، لكن انتبه للتوقيت ولمن يمكنه المشاهدة.',
  },
}
